import { useTheme } from "../context/ThemeContext";
import subscriptionStatus from "../components/SubscriptionStatus.js";
import {
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Paper,
  TextField,
  CircularProgress,
  ListItemText,
  ListItem,
  List,
  Divider,
  Alert, // Added Alert component for error display
} from "@mui/material";
import {
  QueryStats,
  DateRange,
  ShoppingBag,
  TrendingUp,
  MonetizationOn,
  Storage,
  Delete,
  PersonAdd,
} from "@mui/icons-material";
import { fetchDataFromAPI } from "../components/api.js";

// --- ERP & Real-Time Analytics Unified Color Palette ---
const COLORS = {
  // Master Palette from the Real-Time Dashboard
  primary: "#2E4053", // Previously PRIMARY_ACCENT, CHART_EARNINGS_COLOR
  secondary: "#A6AAAD",
  background: "#D1D8E0",
  textPrimary: "#34495E", // Previously DARK_TEXT (similar)
  textSecondary: "#64748B", // Previously SECONDARY_TEXT (same)
  cardBg: "#FFFFFF", // Previously CARD_BG (same)

  // Chart specific colors (re-mapped to the new palette)
  CHART_SALES_COLOR: "#00BFA5", // Retaining the professional green/cyan
  CHART_PROFIT_COLOR: "#FF9800", // Retaining the standard warning/profit color
  CHART_CANCELLATION_COLOR: "#D32F2F", // Red for loss/cancellation
};

// Re-using the DashboardCard component structure for style consistency (defined in the other component)
// For simplicity in this file, we create a local KPI card helper based on the desired new styles
const KPICard = ({ label, value, color, icon: Icon }) => (
  <Card
    elevation={4}
    sx={{
      borderRadius: 2,
      borderLeft: `5px solid ${color}`,
      backgroundColor: COLORS.cardBg,
      transition: "transform 0.2s, box-shadow 0.2s",
      "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: "0 10px 15px rgba(0, 0, 0, 0.15)", // Equivalent to hover:shadow-xl
      },
      minHeight: 120,
    }}
  >
    <CardContent
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Box>
        <Typography
          variant="h6"
          color={COLORS.textSecondary}
          gutterBottom
          sx={{ fontSize: "0.9rem" }}
        >
          {label}
        </Typography>
        <Typography
          variant="h5"
          sx={{ fontWeight: "bold", color: COLORS.textPrimary }}
        >
          {value}
        </Typography>
      </Box>
      <Box sx={{ color: color }}>
        <Icon sx={{ fontSize: 40 }} />
      </Box>
    </CardContent>
  </Card>
);

const Home = () => {
  const formatDateForQuery = (date) => {
    const formattedDate = new Date(date);
    return (
      formattedDate.getFullYear() +
      "-" +
      String(formattedDate.getMonth() + 1).padStart(2, "0") +
      "-" +
      String(formattedDate.getDate()).padStart(2, "0")
    );
  };

  const isSubscribed = subscriptionStatus();
  const { theme } = useTheme();

  // Initialization logic
  const startDateStateTemp = new Date();
  startDateStateTemp.setMonth(startDateStateTemp.getMonth() - 1); // 1 month ago
  const formattedStartDate = startDateStateTemp.toISOString().split("T")[0];

  const [startDate, setStartDate] = useState(formattedStartDate);
  const [endDate, setEndDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [currency, setCurrency] = useState("");
  const [data, setData] = useState({
    sales: 0,
    orders: 0,
    profit: 0,
    earnings: 0,
    cancellations: 0,
    newCustomers: 0,
    expenses: 0,
    materials: [],
    chartData: [],
  });

  // Use the theme background if subscribed, otherwise fallback to the unified LIGHT_BG
  const backgroundColor = isSubscribed ? theme.background : COLORS.background; // Use unified background color

  useEffect(() => {
    // Currency extraction logic
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decodedToken = JSON.parse(atob(token.split(".")[1]));
        const userCurrency = decodedToken?.currency || "USD"; // Fallback to USD
        setCurrency(userCurrency);
      } catch (error) {
        console.error("Error extracting currency from token:", error);
        setCurrency("USD");
      }
    }
    fetchData();
  }, []);

  const fetchData = async (page = 1, itemsPerPage = 10) => {
    try {
      setLoading(true);
      setError("");
      const token = localStorage.getItem("token");

      const defaultStartDateTemp = new Date();
      defaultStartDateTemp.setMonth(defaultStartDateTemp.getMonth() - 1);
      const start = formatDateForQuery(startDate || defaultStartDateTemp);
      const end = formatDateForQuery(endDate || new Date());

      const pageNum = Number.isNaN(Number(page)) ? 1 : Number(page);
      const limitNum = Number.isNaN(Number(itemsPerPage))
        ? 10
        : Number(itemsPerPage);

      const createFilters = (startKey, endKey) =>
        new URLSearchParams({
          page: pageNum.toString(),
          limit: limitNum.toString(),
          [startKey]: start,
          [endKey]: end,
        }).toString();

      const orderFilters = createFilters("order_date_start", "order_date_end");
      const earningsFilters = createFilters(
        "earning_date_start",
        "earning_date_end"
      );
      const expensesFilters = createFilters(
        "expense_date_start",
        "expense_date_end"
      );
      const customersFilters = createFilters(
        "created_at_start",
        "created_at_end"
      );

      const [
        ordersResponse,
        customersResponse,
        earningsResponse,
        expensesResponse,
        materialsResponse,
      ] = await Promise.all([
        fetchDataFromAPI(`/api/orders?${orderFilters}`, {}, token),
        fetchDataFromAPI(`/api/customers?${customersFilters}`, {}, token),
        fetchDataFromAPI(`/api/earnings?${earningsFilters}`, {}, token),
        fetchDataFromAPI(`/api/expenses?${expensesFilters}`, {}, token),
        fetchDataFromAPI(`/api/materials`, {}, token),
      ]);

      const OrdersCount = ordersResponse?.pagination?.totalItems ?? 0;
      const CustomersCount = customersResponse?.pagination?.totalItems ?? 0;

      const sales =
        ordersResponse?.data?.filter((order) => order.status === "shipped")
          .length ?? 0;
      const cancellations =
        ordersResponse?.data?.filter((order) => order.status === "cancelled")
          .length ?? 0;

      const formatCurrency = (amount) =>
        new Intl.NumberFormat("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(amount);

      // Profit
      const totalProfitValue =
        earningsResponse?.data?.reduce(
          (acc, item) => acc + (parseFloat(item.profit) || 0),
          0
        ) || 0;
      const profit = formatCurrency(totalProfitValue);

      // Earnings
      const earningsTotalValue =
        earningsResponse?.data?.reduce(
          (acc, item) => acc + (parseFloat(item.total_earning) || 0),
          0
        ) || 0;
      const earnings = formatCurrency(earningsTotalValue);

      // Expenses
      const expensesTotalValue =
        expensesResponse?.data?.reduce(
          (acc, item) => acc + (parseFloat(item.Amount) || 0),
          0
        ) || 0;
      const expenses = formatCurrency(expensesTotalValue);

      // Materials
      const materials = Array.isArray(materialsResponse?.data)
        ? materialsResponse.data.map((material) => ({
            name: material.name,
            quantityRemaining: material.quantity_remaining,
          }))
        : [];

      // Chart Data Aggregation
      const chartData =
        earningsResponse?.data?.reduce((acc, entry) => {
          const dateKey = formatDateForQuery(entry.earning_date);
          const existingEntry = acc.find((item) => item.date === dateKey);

          const currentProfit = parseFloat(entry.profit) || 0;
          const currentEarnings = parseFloat(entry.total_earning) || 0;

          // Placeholder for Sales (needs actual daily transactional data)
          const currentSales =
            ordersResponse?.data?.filter(
              (order) =>
                formatDateForQuery(order.order_date) === dateKey &&
                order.status === "shipped"
            ).length || 0;

          if (existingEntry) {
            existingEntry.earnings += currentEarnings;
            existingEntry.profit += currentProfit;
            existingEntry.sales += currentSales;
          } else {
            acc.push({
              date: dateKey,
              sales: currentSales,
              earnings: currentEarnings,
              profit: currentProfit,
            });
          }
          return acc;
        }, []) ?? [];

      // Sort chart data by date for proper rendering
      chartData.sort((a, b) => new Date(a.date) - new Date(b.date));

      // Update State
      setData({
        sales,
        orders: OrdersCount,
        profit,
        earnings,
        cancellations,
        newCustomers: CustomersCount,
        expenses,
        materials,
        chartData,
      });
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Failed to fetch dashboard data. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  const metricData = [
    {
      label: "Total Sales",
      value: data.sales,
      color: COLORS.primary,
      type: "count",
      icon: ShoppingBag,
    },
    {
      label: "Total Orders",
      value: data.orders,
      color: COLORS.primary,
      type: "count",
      icon: QueryStats,
    },
    {
      label: "Net Profit",
      value: data.profit,
      color: COLORS.CHART_SALES_COLOR, // Green
      type: "money",
      icon: TrendingUp,
    },
    {
      label: "Gross Earnings",
      value: data.earnings,
      color: COLORS.primary,
      type: "money",
      icon: MonetizationOn,
    },
    {
      label: "Cancellations",
      value: data.cancellations,
      color: COLORS.CHART_CANCELLATION_COLOR, // Red
      type: "count",
      icon: Delete,
    },
    {
      label: "New Customers",
      value: data.newCustomers,
      color: COLORS.primary,
      type: "count",
      icon: PersonAdd,
    },
    {
      label: "Total Expenses",
      value: data.expenses,
      color: COLORS.CHART_PROFIT_COLOR, // Orange/Warning
      type: "money",
      icon: MonetizationOn,
    },
  ];

  return (
    <Box
      sx={{
        padding: { xs: "15px", md: "30px" },
        backgroundColor: backgroundColor, // Use context theme/fallback
        minHeight: "100vh",
        fontFamily: "Poppins, sans-serif",
      }}
    >
      <Typography
        variant="h4"
        sx={{
          fontWeight: 600,
          marginBottom: "20px",
          color: COLORS.textPrimary,
          display: "flex",
          alignItems: "center",
        }}
      >
        <QueryStats sx={{ mr: 1, color: COLORS.primary }} /> ERP Dashboard
      </Typography>

      {/* Date Filter/Picker Section - Using Paper with Box Shadow styling */}
      <Paper
        elevation={0} // Remove standard elevation
        sx={{
          p: { xs: 2, md: 3 },
          mb: 4,
          display: "flex",
          gap: { xs: 1, md: 2 },
          alignItems: "center",
          backgroundColor: COLORS.cardBg,
          borderRadius: 2,
          boxShadow: "0 6px 10px rgba(0, 0, 0, 0.08)", // Match DashboardCard style
        }}
      >
        <DateRange sx={{ color: COLORS.primary, mr: 1 }} />
        <Typography
          variant="subtitle1"
          sx={{ color: COLORS.primary, fontWeight: 600 }}
        >
          Filter Period:
        </Typography>
        <TextField
          label="Start Date"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          sx={{ flexGrow: 1, maxWidth: 200 }}
        />
        <TextField
          label="End Date"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          sx={{ flexGrow: 1, maxWidth: 200 }}
        />
        <Button
          variant="contained"
          onClick={fetchData}
          disabled={loading}
          sx={{
            height: "40px",
            backgroundColor: COLORS.primary,
            color: "white",
            minWidth: 100,
            "&:hover": { backgroundColor: "#1A2A38" }, // Darker hover state
          }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : "Apply"}
        </Button>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Stats Section (KPI Cards) */}
      <Grid container spacing={3} mb={4}>
        {metricData.map((metric, index) => {
          const formattedValue =
            metric.type === "money"
              ? `${currency} ${metric.value}`
              : (metric.value || 0).toLocaleString();

          return (
            <Grid item xs={12} sm={6} md={3} lg={3} key={index}>
              <KPICard
                label={metric.label}
                value={formattedValue}
                color={metric.color}
                icon={metric.icon}
              />
            </Grid>
          );
        })}
      </Grid>

      <Grid container spacing={3}>
        {/* Line Chart Section - Using Paper with Box Shadow styling */}
        <Grid item xs={12} md={8}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              backgroundColor: COLORS.cardBg,
              boxShadow: "0 6px 10px rgba(0, 0, 0, 0.08)", // Match DashboardCard style
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: COLORS.textPrimary, fontWeight: 600 }}
            >
              Sales, Earnings & Profit Trend
            </Typography>
            <Divider sx={{ mb: 2, borderColor: COLORS.background }} />
            <ResponsiveContainer width="100%" height={350}>
              <AreaChart data={data.chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis dataKey="date" stroke={COLORS.textSecondary} />
                <YAxis stroke={COLORS.textSecondary} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: COLORS.cardBg,
                    border: `1px solid ${COLORS.primary}`,
                    borderRadius: "4px",
                  }}
                  labelStyle={{ fontWeight: "bold", color: COLORS.textPrimary }}
                />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="sales"
                  name="Sales"
                  stroke={COLORS.CHART_SALES_COLOR}
                  fill={COLORS.CHART_SALES_COLOR}
                  fillOpacity={0.4}
                />
                <Area
                  type="monotone"
                  dataKey="earnings"
                  name="Earnings"
                  stroke={COLORS.primary}
                  fill={COLORS.primary}
                  fillOpacity={0.4}
                />
                <Area
                  type="monotone"
                  dataKey="profit"
                  name="Profit"
                  stroke={COLORS.CHART_PROFIT_COLOR}
                  fill={COLORS.CHART_PROFIT_COLOR}
                  fillOpacity={0.4}
                />
              </AreaChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Material Inventory Section - Using Paper with Box Shadow styling */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              backgroundColor: COLORS.cardBg,
              height: "100%", // Match height to grid sibling
              maxHeight: "450px",
              overflow: "hidden",
              boxShadow: "0 6px 10px rgba(0, 0, 0, 0.08)", // Match DashboardCard style
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                color: COLORS.textPrimary,
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
              }}
            >
              <Storage sx={{ mr: 1, color: COLORS.primary }} /> Low Inventory
              Alert
            </Typography>
            <Divider sx={{ mb: 1, borderColor: COLORS.background }} />

            <Box sx={{ maxHeight: "350px", overflowY: "auto" }}>
              <List dense>
                {data.materials.length > 0 ? (
                  data.materials.map((material, index) => (
                    <ListItem
                      key={index}
                      sx={{
                        borderBottom: `1px solid ${COLORS.background}`,
                        paddingY: 1,
                        "&:hover": { backgroundColor: COLORS.background },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: "medium",
                              color: COLORS.textPrimary,
                            }}
                          >
                            {material.name}
                          </Typography>
                        }
                        secondary={
                          <Typography
                            variant="body2"
                            sx={{
                              color:
                                material.quantityRemaining < 10
                                  ? COLORS.CHART_CANCELLATION_COLOR // Red Alert
                                  : COLORS.textSecondary,
                            }}
                          >
                            Remaining: {material.quantityRemaining} units
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))
                ) : (
                  <Typography
                    variant="body2"
                    color={COLORS.textSecondary}
                    sx={{ p: 2 }}
                  >
                    No material data available or inventory is fully stocked.
                  </Typography>
                )}
              </List>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Home;
